
import { Component } from 'vue-property-decorator';
import Vue from 'vue';
import CodeLogin from './CodeLogin';
import Spinner from '../../util/Spinner';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { handleError, initClient } from '../../../api';
import {
  ApiException,
  GroupUserExternal,
  SurveyCodeAuthenticationRequest,
  SurveyCodeUiClient,
  SurveyResultMeta,
  SurveyView,
} from '../../backend/backendapi';
import { formatDateTime, isNoMail } from '../../../util';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import Toasted from 'vue-toasted';
import SurveyJSComponent, { SurveyUser } from '../SurveyJSComponent';
import CenterOnParent from '../../util/CenterOnParent';
import AttachmentsButtonIfPresent from '../AttachmentsButtonIfPresent';
import TabButton from '../../util/TabButton';
import QrCode from '../../util/QrCode';
import { Like } from '../../../util/Like';
import StillValidConfirmModal from './StillValidConfirmModal';
import { getLocalizedSurveyName } from '../utils';

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    AttachmentsButtonIfPresent,
    CenterOnParent,
    CodeLogin,
    QrCode,
    Spinner,
    StillValidConfirmModal,
    SurveyJSComponent,
    TabButton,
    VueGoodTable,
  },
})
export default class Code extends Vue {
  readonly api = initClient(SurveyCodeUiClient);
  entryQrCode: string | null = null;
  codeImageUrl: string | null = null;
  loading = false;
  surveys: SurveyView[] = [];
  surveysMeta: SurveyResultMeta[] = [];
  tab: 'login' | 'list' | 'survey' | 'qr' = 'login';
  user: GroupUserExternal | null = null;
  surveyComponentProps: { surveyId: string, started: Date, user: SurveyUser }
      = { surveyId: '', started: new Date(), user: { firstName: '', lastName: '', email: '', company: '' } };

  isNoMail = isNoMail;

  get columnsConfig(): any[] {
    return [
      {
        label: this.$i18n.t('survey.surveyResultMeta.model.surveyName').toString(),
        field: (e: SurveyResultMeta) => getLocalizedSurveyName(e.surveyNames, e.surveyName, this.$i18n.locale),
        isSurveyNameColumn: true,
      }, {
        label: this.$t('survey.surveyResultMeta.model.date').toString(),
        // e.date is nullable, but somehow the openapi generator does not properly generate the spec
        // also: if returned value is undefined, vgt does not render the cell at all, so we return null here in case the date is undefined
        field: (e: SurveyResultMeta) => e.date ?? null,
        type: 'date',
        formatFn: (e: Date | undefined) => !e ? this.$t('general.unknown').toString() : formatDateTime(e),
      }, {
        label: this.$t('survey.surveyResultMeta.model.passed').toString(),
        field: 'passed',
      }, {
        label: this.$t('survey.surveyResultMeta.model.validInPeriod').toString(),
        field: (e: SurveyResultMeta) => e.validInPeriod,
        formatFn: (value: boolean) => this.$t(value ? 'yes' : 'no').toString(),
      }, {
        label: this.$t('survey.surveyResultMeta.model.validTil').toString(),
        type: 'date',
        field: (e: SurveyResultMeta) => e.validTil,
        formatFn: (e: Date | undefined) => !e ? '�' : formatDateTime(e),
      }, {
        label: this.$i18n.t('survey.code.list.actions').toString(),
        field: 'actions',
        width: '10em',
      },
    ];
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  async mounted(): Promise<void> {
    this.loading = true;
    try {
      //try loading user, if we are logged in, the user variable will be set
      try {
        await this.loadUser();
      } catch (err) {
      }

      if (this.user) {
        await this.onAuthenticated();
      }
    } finally {
      this.loading = false;
    }
  }

  async loadSurveysMeta(): Promise<void> {
    const res = await this.api.getSurveysResultMeta();
    this.surveysMeta = res.surveyResultMetas;
    this.entryQrCode = res.entryQrCode ?? null;

    try {
      const imageRes = await this.api.getCodeImage();
      this.codeImageUrl = imageRes?.url ?? null; 
    } catch (error) {
      this.codeImageUrl = null;
    }
  }

  async loadUser(): Promise<void> {
    this.user = null;
    this.user = await this.api.getUser();
  }

  async logout(): Promise<void> {
    this.tab = 'login';
    this.surveysMeta = [];
    this.user = null;
    this.codeImageUrl = null;

    try {
      await this.api.deauthenticate();
    } catch (error) {
    }
  }

  async onAuthenticated(): Promise<void> {
    this.tab = 'list';
    await this.loadSurveysMeta();
  }

  async onLogin(code: number, firstLetterOfLastName: string): Promise<void> {
    this.loading = true;
    try {
      await this.api.authenticate(SurveyCodeAuthenticationRequest.fromJS({ code: code, firstLetterOfLastName: firstLetterOfLastName }));
      await this.loadUser();
      await this.onAuthenticated();
    } catch (error) {
      if ((error as ApiException).status === 401) {
        this.$toasted.error(this.$t('survey.code.codeInvalid').toString());
      } else {
        handleError(this.$toasted, this.$i18n, error);
      }
    } finally {
      this.loading = false;
    }
  }

  async onSurveyBack(): Promise<void> {
    this.tab = 'list';
    try {
      await this.loadUser();
    } catch (error) {
      //fetch user data failed, probably cookie timeout, got back to login
      this.tab = 'login';
      return;
    }
    await this.loadSurveysMeta();
  }

  openSurvey(meta: Like<SurveyResultMeta>, ignoreStillValid: boolean = false): void {
    if (!this.user) {
      return;
    }

    if (meta.validInPeriod && !ignoreStillValid) {
      (this.$refs.stillValidConfirmModal as StillValidConfirmModal).open(meta);
      return;
    }
    (this.$refs.stillValidConfirmModal as StillValidConfirmModal).dismiss(meta);
    this.surveyComponentProps = {
      surveyId: meta.surveyId,
      started: new Date(),
      user: {
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        email: this.user.email,
        // company: this.user.company,
        company: '',
      },
    };
      this.tab = 'survey';
  }

  getFormattedInstructions(): string {
    const baseText = this.$t('survey.code.myInfo').toString();
    const iconHtml = '(<i class="fa fa-arrow-right mx-1"></i>)';
    return baseText.replace('{arrowIcon}', iconHtml);
  }

   getWelcome(): string {
    const welcomeText = this.$t('survey.code.welcome').toString() + ' ' + this.user?.salutation + ' ' + this.user?.firstName + ' ' + this.user?.lastName + ' !';
    return welcomeText;
  }
}
